import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Sparkles, Home as HomeIcon, Clock } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";
import SakuraPetals from "@/components/SakuraPetals";
import { api } from "@/lib/api";

/**
 * After Stripe Checkout returns to /pay/success?session_id=... we poll the
 * backend up to ~60s for the session's payment_status.
 *
 * Important rules (to avoid duplicate charges):
 *  - We NEVER show a "try again" / "pay again" button on this page. If Stripe
 *    already received the payment, sending the user back to /pay would create
 *    a second charge.
 *  - If we can't confirm within the polling window, we show a friendly
 *    "still processing" state with contact info — the webhook will update the
 *    record asynchronously and Stripe will email the customer a receipt.
 */
export default function PaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({
    status: "checking",
    payment_status: "",
    amount_total: 0,
    currency: "usd",
    attempts: 0,
  });
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: "no_session" }));
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (stopped.current) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        setState({ ...data, attempts });
        if (data.payment_status === "paid") {
          stopped.current = true;
          return;
        }
      } catch (_) {}
      if (attempts >= 30) {
        // ~60s — give up but show a friendly "still processing" message,
        // never an error or retry button.
        stopped.current = true;
        setState((s) => ({ ...s, attempts, status: "pending_confirmation" }));
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped.current = true; };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const stillProcessing = !paid && state.status === "pending_confirmation";
  const noSession = state.status === "no_session";

  return (
    <div data-testid="page-pay-success">
      <section className="section relative overflow-hidden">
        <SakuraPetals count={6} />
        <div className="mx-auto max-w-xl relative">
          <div className="card-soft p-8 md:p-12 text-center">
            {paid ? (
              <>
                <span className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-sakura-200 text-sakura-500">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-sakura-ink">
                  Payment received! 🌸
                </h1>
                <p className="mt-3 text-sakura-soft">
                  Your deposit of{" "}
                  <b className="text-sakura-ink">
                    ${((state.amount_total || 0) / 100).toFixed(2)}
                  </b>{" "}
                  has been confirmed. A receipt is on its way to your inbox, and
                  I'll be in touch with next steps soon.
                </p>
                <SakuraDivider />
                <div className="flex flex-wrap justify-center gap-3" data-testid="pay-success-actions">
                  <Link to="/" className="btn-secondary" data-testid="pay-success-home">
                    <HomeIcon className="h-4 w-4" /> Back home
                  </Link>
                  <Link to="/custom-orders" className="btn-primary" data-testid="pay-success-order">
                    <Sparkles className="h-4 w-4" /> Submit another inquiry
                  </Link>
                </div>
              </>
            ) : stillProcessing ? (
              <>
                <span className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-sakura-200 text-sakura-500">
                  <Clock className="h-8 w-8" />
                </span>
                <h1 className="mt-5 font-serif text-3xl text-sakura-ink" data-testid="pay-pending-heading">
                  Thank you — your payment is processing 🌸
                </h1>
                <p className="mt-3 text-sakura-soft">
                  Stripe is still finalizing your payment on its end. This page
                  hasn't seen final confirmation yet, but{" "}
                  <b className="text-sakura-ink">don't worry and please don't pay again</b>.
                  If your card was charged, you'll receive a receipt by email
                  within a few minutes and I'll follow up shortly.
                </p>
                <SakuraDivider />
                <p className="text-sm text-sakura-soft">
                  Questions? Reach out to{" "}
                  <a
                    href="mailto:hello@sakuratreats.com"
                    className="text-sakura-500 underline underline-offset-2"
                  >
                    hello@sakuratreats.com
                  </a>{" "}
                  with the time of your payment and I'll confirm right away.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link to="/" className="btn-secondary" data-testid="pay-pending-home">
                    <HomeIcon className="h-4 w-4" /> Back home
                  </Link>
                  <a
                    href="mailto:hello@sakuratreats.com?subject=Sakura%20Treats%20Payment%20Confirmation"
                    className="btn-primary"
                    data-testid="pay-pending-email"
                  >
                    <Mail className="h-4 w-4" /> Email me
                  </a>
                </div>
              </>
            ) : noSession ? (
              <>
                <span className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-sakura-200 text-sakura-500">
                  <HomeIcon className="h-8 w-8" />
                </span>
                <h1 className="mt-5 font-serif text-3xl text-sakura-ink">
                  No payment session found
                </h1>
                <p className="mt-3 text-sakura-soft">
                  Looks like you landed here directly. If you completed a
                  payment, please check your email for the receipt.
                </p>
                <div className="mt-6">
                  <Link to="/" className="btn-secondary">
                    <HomeIcon className="h-4 w-4" /> Back home
                  </Link>
                </div>
              </>
            ) : (
              <>
                <span className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-sakura-200 text-sakura-500 animate-pulse">
                  <Sparkles className="h-8 w-8" />
                </span>
                <h1 className="mt-5 font-serif text-2xl text-sakura-ink">
                  Confirming your payment…
                </h1>
                <p className="mt-2 text-sakura-soft text-sm">
                  Hang tight, this usually takes just a few seconds.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
